import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Snackbar } from "@mui/material";

import {
  CloseButton,
  ContentWrapper,
  IconWrapper,
  Message,
  ProgressBar,
  Title,
  ToastContainer,
  ToastVariant,
} from "./styles";

const VARIANT_ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircleOutlinedIcon />,
  error: <ErrorOutlineIcon />,
  warning: <WarningAmberIcon />,
  info: <InfoOutlinedIcon />,
};

const VARIANT_TITLE_KEYS: Record<ToastVariant, string> = {
  success: "toast.titles.success",
  error: "toast.titles.error",
  warning: "toast.titles.warning",
  info: "toast.titles.info",
};

export type ToastProps = {
  open: boolean;
  message: string | React.ReactNode;
  variant: ToastVariant;
  onClose: () => void;
  duration?: number;
  showIcon?: boolean;
  showProgress?: boolean;
  title?: string;
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
};

const Toast: React.FC<ToastProps> = ({
  open,
  message,
  variant,
  onClose,
  duration = 3000,
  showIcon = true,
  showProgress = true,
  title,
  anchorOrigin = { vertical: "bottom", horizontal: "center" },
}) => {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
      setIsExiting(false);
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (!open || duration <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      handleClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [open, duration, handleClose]);

  const displayTitle = title || t(VARIANT_TITLE_KEYS[variant]);

  return (
    <Snackbar
      open={open}
      anchorOrigin={anchorOrigin}
      onClose={handleClose}
      sx={{
        "& .MuiSnackbarContent-root": {
          padding: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <ToastContainer $variant={variant} $isExiting={isExiting}>
        {showIcon && (
          <IconWrapper $variant={variant}>{VARIANT_ICONS[variant]}</IconWrapper>
        )}
        <ContentWrapper>
          <Title>{displayTitle}</Title>
          <Message>{message}</Message>
        </ContentWrapper>
        <CloseButton onClick={handleClose} aria-label={t("toast.close")}>
          <CloseIcon />
        </CloseButton>
        {showProgress && duration > 0 && (
          <ProgressBar $variant={variant} $duration={duration} />
        )}
      </ToastContainer>
    </Snackbar>
  );
};

export default Toast;
export type { ToastVariant };
